import React, { useContext, useRef } from 'react';
import { ConditionType, Filter, filterOperatorOptions, RelationsProps } from '@fabric-ui/components';
import { TokenSuggestion } from './components/TokenFilterInput/types';

export interface CustomFieldInputProps {
  actualFilterValue: Filter;
  autoFocus?: boolean;
  disabled?: boolean;
  enableFilter?: boolean;
  fieldName?: string | null;
  filter: Filter;
  isOperatorLockedByToken?: boolean;
  isVertical: boolean;
  readOnly?: boolean;
  setEffectiveFilter: (filter: Filter) => void;
  setFilter: (filter: Filter) => void;
  showListTab?: boolean;
  size: 'xSmall' | 'small' | 'medium';
  typeCondition?: ConditionType;
  typeName?: string;
}

export interface CustomFieldHandler {
  /** Recognize this custom field from an existing filter object. */
  matchFilter?: (filter: Filter) => boolean;
  /** Create the empty filter for this field when selected from the dropdown. */
  getEmptyFilter?: () => Filter;
  /** Component that renders the value input for this field. */
  component: React.FC<CustomFieldInputProps>;
  /** Focus the value input when this field is selected from the dropdown. */
  autoFocusOnSelect?: boolean;
  /** Icon shown in the dropdown list and the selected field chip. */
  icon?: React.ReactNode;
}

export interface ExpressionBuilder {
  fields?: any[];
  isLoadingFields?: boolean;
  defaultField?: string | null;
  canAddNewFields?: boolean;
  canSelectFieldType?: boolean;
  supportParser?: boolean;
  operatorOptions?: typeof filterOperatorOptions;
  relationsProps?: RelationsProps;
  disableMessage?: string;
  isOverExpressionsLimit?: boolean;
  fieldSelectionMessage?: string;
  fieldOnly?: boolean;
  hasListTab?: boolean;
  isHistorical?: boolean;
  shouldShowIcon?: (operand: any) => boolean;
  defaultMode?: 'list' | 'condition';
  excludeNullFromList?: boolean;
  fieldExtraOptions?: Record<string, string[]>;
  timeRange?: Date[];
  fieldsListAdornment?: React.ReactNode;
  fieldDescription?: string;
  icon?: React.ReactNode;
  canDeleteLast?: boolean;
  readOnly?: boolean;
  tokenSuggestions?: TokenSuggestion[];
  ingressModeProps?: { showToggle: boolean; multiSelect: boolean; isIconCollapsed?: boolean };
  customFieldHandlers?: Record<string, CustomFieldHandler>;
}

export const defaultExpressionBuilder: ExpressionBuilder = {};

export const expressionBuilderKeys: (keyof ExpressionBuilder)[] = [
  'fields',
  'isLoadingFields',
  'defaultField',
  'canAddNewFields',
  'canSelectFieldType',
  'supportParser',
  'operatorOptions',
  'relationsProps',
  'disableMessage',
  'isOverExpressionsLimit',
  'fieldSelectionMessage',
  'fieldOnly',
  'hasListTab',
  'isHistorical',
  'shouldShowIcon',
  'defaultMode',
  'excludeNullFromList',
  'fieldExtraOptions',
  'timeRange',
  'fieldsListAdornment',
  'fieldDescription',
  'icon',
  'canDeleteLast',
  'readOnly',
  'tokenSuggestions',
  'ingressModeProps',
  'customFieldHandlers',
];

export const pickExpressionBuilder = <T extends object>(props: T): [ExpressionBuilder, Omit<T, keyof ExpressionBuilder>] => {
  const expressionBuilder: any = {};
  const rest: any = {};
  (Object.keys(props) as Array<keyof T>).forEach(key => {
    const stringKey = key as string;
    if (expressionBuilderKeys.includes(stringKey as any)) {
      const value = props[key];
      if (value !== undefined) expressionBuilder[stringKey] = value;
    } else {
      rest[stringKey] = props[key];
    }
  });
  return [expressionBuilder as ExpressionBuilder, rest as Omit<T, keyof ExpressionBuilder>];
};

const ExpressionBuilderContext = React.createContext<ExpressionBuilder>(defaultExpressionBuilder);

const isShallowEqual = (a: ExpressionBuilder, b: ExpressionBuilder): boolean => {
  const aKeys = Object.keys(a) as (keyof ExpressionBuilder)[];
  const bKeys = Object.keys(b) as (keyof ExpressionBuilder)[];
  return aKeys.length === bKeys.length && aKeys.every(key => a[key] === b[key]);
};

// Callers (NewExpressionBuilder, FieldFilterBox, FieldFilter) rebuild the `options` object on every
// render via pickExpressionBuilder, so its reference changes even when none of its individual values
// did (each of those values is already independently memoized upstream). Since a context value change
// re-renders every consumer regardless of React.memo, we keep the last value and only replace it when
// one of its fields actually changed, so consumers can bail out normally. A shallow comparison is
// sufficient (and cheap even with a large `fields` list) because the bug is container-object churn,
// not changes to the field values themselves.
const useStableExpressionBuilder = (options: ExpressionBuilder): ExpressionBuilder => {
  const ref = useRef(options);
  if (!isShallowEqual(ref.current, options)) {
    ref.current = options;
  }
  return ref.current;
};

export const ExpressionBuilderProvider: React.FC<{ options?: ExpressionBuilder; children: React.ReactNode }> = ({
  options = defaultExpressionBuilder,
  children,
}) => {
  const stableOptions = useStableExpressionBuilder(options);
  return <ExpressionBuilderContext.Provider value={stableOptions}>{children}</ExpressionBuilderContext.Provider>;
};

export const useExpressionBuilder = () => useContext(ExpressionBuilderContext);
